import map from "../map";
import { frameProcs } from "../animation";
import schema from "../schema/index";
import logLevels from "../schema/logLevels";
import perf from "../perf";
import * as silhouette from "./silhouette";
import { applyVisibility, setVisibilityChanged } from "./visibility";

const circum = Math.PI * 2;
const distanceSpacing = 0;
const polarMode = false;
const ragRadius = 6;

export let mainBounds = [-100, -100, 100, 100];

// Room kept in the camera's bounding box for children that are not there yet,
// keyed by parent object type.
//
// Scopes live for seconds and hang off a role beyond its edge, so each one that
// arrives pushes the box out and each one that goes pulls it back. The camera
// fits that box, so a branch that is merely busy makes the whole map breathe.
//
// The box therefore ignores them entirely and holds the room open on every role
// that could have one instead, which makes it depend on the shape of the estate
// rather than on what is running through it. A role fanning out a dozen scopes
// at once puts its outermost ones past the reservation and so past the fitted
// view; that is the trade, and it is worth it against the map lurching every
// time a request arrives.
//
// Unattached is what separates the transient children from the structural ones:
// scopes are the only type that comes and goes without the tree changing shape
// around it, and it is the only type the layout draws with no connection line.
const reservedExtents = {};

function isUnattached(type) {
  return schema.objectTypes[type].layout.unattached === true;
}

function reservedExtentFor(type) {
  let extent = reservedExtents[type];
  if (extent !== undefined) return extent;

  extent = 0;
  const collections = schema.objectTypes[type].collections || {};

  for (const key in collections) {
    const childType = schema.objectTypes[collections[key].objectType];
    const layout = childType && childType.layout;
    // A radius that varies per object cannot be resolved without an object to
    // ask, and nothing unattached has one.
    if (!layout || !layout.unattached) continue;
    if (typeof layout.radius !== "number") continue;

    // Matching what setDistance gives the child: it clears the parent's own
    // radius — unzoomed, which is the figure setDistance works from — by its own
    // radius and its stacking gap, then its own radius again to reach its far
    // side.
    const reach = layout.radius * 2 + (layout.stackSpacing || distanceSpacing);
    if (reach > extent) extent = reach;
  }

  reservedExtents[type] = extent;
  return extent;
}

let layoutRoot = "*";
let lineContainer = null;

// How far the arrangement has turned so far, in radians. Advanced every frame by
// animateAndLineCartesian, and used as the start angle for every solve.
let spinAngle = 0;

export function setLineContainer(x) {
  lineContainer = x;

  // Ahead of solveLayout, so a node hidden this frame is out of the tree before
  // the angles are handed round rather than a frame later.
  frameProcs.updateVisibility = updateVisibility;

  // Registered here, from an effect that runs before the one setting up autoSize,
  // so the solve lands ahead of the frame procs that consume its output.
  frameProcs.solveLayout = solveLayoutIfDirty;

  setVisibilityChanged(layout);

  layout();
}

export function setLayoutRoot(x) {
  layoutRoot = x;

  // Recorded rather than applied: the filter panel can hide things too, and the
  // two have to compose. `applyVisibility` is what actually resolves `hidden`.
  for (const objKey in map.objects) {
    map.objects[objKey].focusHidden =
      x != "*" && x != objKey && (x.startsWith(objKey) || objKey == "*");
  }

  layout();
}

// Cheap enough to run unconditionally — it is a walk of the object list with a
// timestamp comparison — and it has to be, because the idle filter goes stale on
// its own as time passes rather than in response to anything.
function updateVisibility() {
  if (applyVisibility()) layout();
}

let layoutDirty = false;
let layoutRequests = 0;
let layoutSolves = 0;

// A solve is four full recursions over the tree. Callers request one per object
// added or removed - and scopes arrive and leave at request rate - so requests
// outrunning solves by a wide margin is the whole point of coalescing.
perf.counter("layoutRequests", () => layoutRequests);
perf.counter("layoutSolves", () => layoutSolves);

function layout() {
  layoutRequests++;
  layoutDirty = true;
}

function solveLayoutIfDirty() {
  if (!layoutDirty) return;
  if (!lineContainer) return;

  const start = map.objects[layoutRoot];
  if (!start) return; // stays dirty; the root may not have arrived yet

  layoutDirty = false;
  layoutSolves++;

  perf.begin("layout");
  try {
    layoutCore(start);
  } finally {
    perf.end("layout");
  }
}

function layoutCore(start) {
  start.tx = 0;
  start.ty = 0;
  if (!start.x) start.x = 0;
  if (!start.y) start.y = 0;

  let parent = start.parent;
  while (parent != null) {
    parent.tx = parent.ty = 0;
    if (!parent.x) parent.x = 0;
    if (!parent.y) parent.y = 0;
    parent = parent.parent;
  }

  start.children.forEach(calcAngles);
  distributeAngles(start.children, circum);
  setDistance(start, null);
  setPositions(start.children, spinAngle);

  /*
  let regions = document.body.select("#regions");
  regions.innerHTML = "";
  makeRegions(start, 0);*/

  frameProcs["animateAndLine"] = (time) => animateAndLine(time);
}

function makeRegions(node, ang) {
  node.children.forEach((node) => {
    makeRegions(node, ang);
    const radProp = schema.objectTypes[node.type].layout.radius;
    const rad = radProp instanceof Function ? radProp(node) : radProp;

    const dist1 = node.distance - rad;
    const dist2 = node.distance + rad;

    const x0 = Math.cos(ang) * dist1;
    const y0 = Math.sin(ang) * dist1;

    const x1 = Math.cos(ang) * dist2;
    const y1 = Math.sin(ang) * dist2;

    ang += node.angle;

    const x2 = Math.cos(ang) * dist2;
    const y2 = Math.sin(ang) * dist2;

    const x3 = Math.cos(ang) * dist1;
    const y3 = Math.sin(ang) * dist1;

    regions
      .append("path")
      .attr("stroke", "black")
      .attr("fill", "white")
      .attr(
        "d",
        `M${x0},${y0} L${x1},${y1} A${dist2} ${dist2} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A${dist1} ${dist1} 0 0 0 ${x0} ${y0} Z`
      );
  });
}

function setDistance(obj, base = null, extra = 0) {
  const {
    layout: { radius, stackSpacing, hug },
  } = schema.objectTypes[obj.type];

  const actualRadius = radius instanceof Function ? radius(obj) : radius;

  obj.extra = extra;

  if (base == null) {
    obj.distance = 0;
  } else {
    // calc minimum if we were just stacked up against the parent
    const stackedMinimum =
      base + actualRadius + (stackSpacing || distanceSpacing) + extra;

    // calc minimum based on the angle we have available to us
    const boundsMinimum =
      obj.angle >= Math.PI ? 0 : actualRadius / Math.tan(obj.angle / 2);

    // get the minimum of the two, that's our best option
    obj.distance =
      stackedMinimum > boundsMinimum ? stackedMinimum : boundsMinimum;
  }
  // Hidden children take no part in any of this. Without that they still claim
  // their share of the circle and still pull their parent outwards, so switching
  // a filter off left a gap in the ring exactly the size of what it removed
  // instead of letting what remains spread out.
  let visibleChildren = 0;
  for (let i = 0; i < obj.children.length; i++) {
    if (!obj.children[i].hidden) visibleChildren++;
  }

  if (!visibleChildren) {
    obj.tzoom = 1;
    return;
  }

  // propagate this to our children
  const ragDistance = visibleChildren > 1 ? ragRadius * 4 : 0;
  obj.children.forEach((c) => {
    if (c.hidden) return;
    setDistance(
      c,
      obj.distance + actualRadius,
      schema.objectTypes[c.type].hasRag ? ragDistance : 0
    );
  });

  const effectiveHug = hug || 0;

  const isRoot = base == null || effectiveHug == 1;

  // now go back up... make each parent go close to its children
  // so, what's the distance of the closest child? (child with minimum distance)
  let minDist = Infinity;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    if (child.hidden) continue;
    const childLayout = schema.objectTypes[child.type].layout;

    const childLayoutRadiusProp = childLayout.radius;
    const childLayoutRadius =
      childLayoutRadiusProp instanceof Function
        ? childLayoutRadiusProp(child)
        : childLayoutRadiusProp;

    const distanceLessRadius =
      child.distance -
      childLayoutRadius * child.tzoom -
      (childLayout.stackSpacing || distanceSpacing) -
      child.extra;
    if (distanceLessRadius < minDist) minDist = distanceLessRadius;
  }

  if (isRoot) {
    // special case for root
    obj.tzoom = minDist / schema.objectTypes[obj.type].layout.radius;
    return;
  }

  // find biggest radius that fits
  const brtf = minDist / (1 / Math.sin(obj.angle / 2) + 1);

  // make sure it wouldn't overlap the parent line
  let r = Math.min(brtf, (minDist - obj.distance) / 2);
  if (r < actualRadius) r = actualRadius;

  // set our distance accordingly
  let d = minDist - r;
  obj.tzoom = r / actualRadius;
  obj.distance = d * (1 - effectiveHug) + obj.distance * effectiveHug;
}

let frm = 0;

function animateAndLine(time) {
  if (polarMode) {
    animateAndLinePolar(time);
  } else {
    animateAndLineCartesian(time);
  }
}

function animateAndLinePolar(time) {
  frm++;
  for (const i of Object.values(map.objects)) {
    if (!i.elem) {
      continue;
    }

    const actualX = Math.cos(i.x) * i.y;
    const actualY = Math.sin(i.x) * i.y;
    const targetX = Math.cos(i.tx) * i.ty;
    const targetY = Math.sin(i.tx) * i.ty;

    const dist = Math.sqrt((actualX - targetX) ** 2 + (actualY - targetY) ** 2);
    if (dist > 1) {
      i.x += ((i.tx - i.x) / dist) * ((dist * time) / 1000);
      i.y += ((i.ty - i.y) / dist) * ((dist * time) / 1000);
      i.f = frm;
      i.elem.attr(
        "transform",
        `translate(${Math.cos(i.x) * i.y},${Math.sin(i.x) * i.y})`
      );
    } else if (!i.f) {
      i.f = frm;
      i.elem.attr(
        "transform",
        `translate(${Math.cos(i.x) * i.y},${Math.sin(i.x) * i.y})`
      );
    }
  }
  for (const i of Object.values(map.objects)) {
    if (i.parent && !schema.objectTypes[i.type].layout.unattached) {
      if (i.linkElem == null) {
        i.linkElem = lineContainer.append("line");
        i.f = i.parent.f = frm;
      }
      const e = i.linkElem;
      if (i.f == frm) {
        e.attr("x1", Math.cos(i.x) * i.y).attr("y1", Math.sin(i.x) * i.y);
      }
      if (i.parent.f == frm) {
        e.attr("x2", Math.cos(i.parent.x) * i.parent.y).attr(
          "y2",
          Math.sin(i.parent.x) * i.parent.y
        );
      }
    }
  }
}

// Writing an attribute is not free even when the value is unchanged: it
// invalidates style for that element and feeds the browser's layout and paint
// work, which is now the bulk of the frame. Most of these writes were setting
// the value that was already there - a settled tree with no log activity still
// wrote seven attributes per link per frame. The last value is cached on the map
// object rather than read back from the DOM, which would force layout.
function setAttrIfChanged(cache, elem, name, value) {
  if (cache[name] === value) return;
  cache[name] = value;
  elem.setAttribute(name, value);
}

function animateAndLineCartesian(time) {
  const now = new Date().getTime();
  frm++;

  // The whole arrangement turns, one revolution every 251 seconds.
  //
  // `setPositions` has always taken the start angle off the clock, but it only
  // runs on a solve — so between solves the map sat still, then stepped round by
  // however long it had been. Turning the eased position and its target together
  // by the same amount here advances it every frame instead.
  //
  // Both, and by the same angle, on purpose: the easing below has a `dist > 1`
  // deadband, so turning only the target would leave each node sitting still
  // until its target had crept a whole unit away and then lurching after it, each
  // at its own moment. Turning both leaves the distance between them untouched,
  // so the easing never sees the rotation at all.
  //
  // The running total is what `setPositions` is given as its start angle, so a
  // solve lands its targets exactly where the turn has already carried
  // everything else — rather than at wherever the wall clock had reached, which
  // is what made the map jump on every solve.
  // How fast a link's traffic level falls back when nothing more arrives.
  const flowFade = Math.exp(-time / 700);

  const spin = time / 40000;
  spinAngle += spin;
  const spinCos = Math.cos(spin);
  const spinSin = Math.sin(spin);

  mainBounds = [null, null, null, null];

  // The callout leader lines need the map's outline to route around it. Filling
  // it here rather than in its own pass costs only the extra call below: this
  // loop already resolves every object's radius and zoom for `mainBounds`.
  silhouette.begin();

  // One snapshot serves both passes; neither adds or removes map objects.
  const objects = Object.values(map.objects);

  // update positions
  for (const i of objects) {
    if (!i.elem) {
      continue;
    }
    if (i.hidden) {
      // Nothing at all. This used to pin hidden objects to the origin, so a scope
      // being filtered flew into the middle of the map on its way out and back
      // again if it returned; nothing draws it while it is hidden, so its position
      // does not matter. `i.f` is deliberately left alone too — marking it as
      // moved would have the connection lines pick up a position it may not have
      // yet, since a node hidden from the moment it arrived has never been placed.
    } else {
      const rx = i.x * spinCos - i.y * spinSin;
      i.y = i.x * spinSin + i.y * spinCos;
      i.x = rx;

      const rtx = i.tx * spinCos - i.ty * spinSin;
      i.ty = i.tx * spinSin + i.ty * spinCos;
      i.tx = rtx;

      const dist = Math.sqrt((i.x - i.tx) ** 2 + (i.y - i.ty) ** 2);
      if (!i.zoom) i.zoom = 0.1;
      const zdist = Math.abs(i.zoom - i.tzoom);

      if (dist > 1) {
        i.x += ((i.tx - i.x) / dist) * ((dist * time) / 200);
        i.y += ((i.ty - i.y) / dist) * ((dist * time) / 200);
      }
      if (zdist > 0.01) {
        i.zoom += ((i.tzoom - i.zoom) / zdist) * ((zdist * time) / 200);
      }

      // Unconditional now: the turn above moves every node every frame, so there
      // is no longer a settled state to skip.
      i.f = frm;
      const tf = `translate(${i.x},${i.y}) scale(${i.zoom})`;
      i.elem.attr("transform", tf);

      // The parsed form rides along for the WebGL renderer: the turn makes the
      // string above fresh for every object every frame, and letting the
      // renderer regex-parse a thousand unique strings a frame thrashed its
      // transform cache. Inert under SVG, where the browser does the parsing.
      i.elem._parsedTf = [i.zoom, 0, 0, i.zoom, i.x, i.y];
      i.elem._parsedTfFor = tf;

      const radiusProp = schema.objectTypes[i.type].layout.radius;
      const radius =
        radiusProp instanceof Function ? radiusProp(i) : radiusProp;

      const drawnRadius = radius * i.zoom;

      // Both of these are the camera's alone. The silhouette below stays on what
      // is actually drawn, scopes included, so callout leaders keep hugging the
      // real outline rather than routing around empty space.
      if (!isUnattached(i.type)) {
        // setDistance places a child off its parent's unzoomed radius, so the
        // reservation is measured from there rather than from the drawn edge.
        const reserved = reservedExtentFor(i.type);
        const boundsRadius = reserved
          ? Math.max(drawnRadius, radius + reserved)
          : drawnRadius;

        const minX = i.x - boundsRadius;
        const maxX = i.x + boundsRadius;
        const minY = i.y - boundsRadius;
        const maxY = i.y + boundsRadius;
        if (minX < mainBounds[0] || mainBounds[0] == null) mainBounds[0] = minX;
        if (maxX > mainBounds[2] || mainBounds[2] == null) mainBounds[2] = maxX;
        if (minY < mainBounds[1] || mainBounds[1] == null) mainBounds[1] = minY;
        if (maxY > mainBounds[3] || mainBounds[3] == null) mainBounds[3] = maxY;
      }

      silhouette.addDisc(i.x, i.y, drawnRadius);
    }
  }

  silhouette.end(time);

  // update connections and RAGs
  for (const i of objects) {
    if (!i.parent) continue;

    const objType = schema.objectTypes[i.type];

    let colour = "#777";

    if (objType.hasRag) {
      if (i.parent.children.length < 2) {
        if (i.ragElem) {
          i.ragElem.remove();
          i.ragElem = null;
          i.ragCircle = null;
          i._ragAttrs = null; // cached values belong to the discarded elements
        }
      } else {
        const parent = i.parent;
        const parentX = parent.x;
        const parentY = parent.y;
        const dX = i.x - parentX;
        const dY = i.y - parentY;
        const dist = Math.sqrt(dX ** 2 + dY ** 2);
        const dirX = dX / dist;
        const dirY = dY / dist;

        const angle = Math.atan2(dirY, dirX);

        const radiusProp = schema.objectTypes[parent.type].layout.radius;
        const radius =
          radiusProp instanceof Function ? radiusProp(parent) : radiusProp;

        const rad = (radius + ragRadius * 2) * i.parent.tzoom;
        const ragX = parentX + dirX * rad - i.x;
        const ragY = parentY + dirY * rad - i.y;

        if (!i.ragElem) {
          const rg = i.elem.addFirstChild("g");
          // Kept so the per-frame fill update does not have to re-find it with a
          // querySelector on every node, every frame.
          i.ragCircle = rg
            .append("circle")
            .attr("r", ragRadius)
            .attr("fill", "black");
          //rg.append("path").attr("d", `M0,${ragRadius*-0.6} L${ragRadius},${ragRadius} H${-ragRadius} Z`).attr("fill", "white");
          const ang = (2 * Math.PI) / 3;
          const x1 = Math.sin(ang) * ragRadius * 0.6;
          const y1 = Math.cos(ang) * ragRadius * 0.6;
          const x2 = Math.sin(ang * 2) * ragRadius * 0.6;
          const y2 = Math.cos(ang * 2) * ragRadius * 0.6;
          const x3 = Math.sin(ang * 3) * ragRadius * 0.6;
          const y3 = Math.cos(ang * 3) * ragRadius * 0.6;
          rg.append("path")
            .attr("d", `M${x1},${y1} L${x2},${y2} L${x3},${y3} Z`)
            .attr("fill", "white");
          i.ragElem = rg;
        }

        const le = i.lastEvents;
        if (le) {
          for (let j = logLevels.length - 1; j >= 0; j--) {
            const last = le[logLevels[j].name];
            if (!last) continue;
            if (last > now - 60000) {
              colour = logLevels[j].colour;
              break;
            }
          }
        }

        const ragCache = i._ragAttrs || (i._ragAttrs = {});

        // Quantised: the badge creeps around its node as the map turns, a
        // fraction of a degree and a few hundredths of a unit a frame, and
        // writing every step re-emitted every badge's geometry every frame.
        // Half a degree and a quarter unit are below anything the badge can
        // visibly show, so the written value repeats and the write no-ops.
        const ragDeg = Math.round((90 + angle / (Math.PI / 180)) * 2) / 2;
        const ragS = Math.round((1 / i.zoom) * 1000) / 1000;
        setAttrIfChanged(
          ragCache,
          i.ragElem,
          "transform",
          `scale(${ragS}) translate(${Math.round(ragX * 4) / 4},${
            Math.round(ragY * 4) / 4
          }) rotate(${ragDeg})`
        );
        setAttrIfChanged(ragCache, i.ragCircle, "fill", colour);
      }
    }

    // connection lines
    if (!objType.layout.unattached) {
      if (i.linkElem == null) {
        i.linkElem = lineContainer.append("line");
        i.f = i.parent.f = frm;
      }
      const e = i.linkElem;
      const linkCache = i._linkAttrs || (i._linkAttrs = {});

      // An end is rewritten only once it has drifted a unit and a half from
      // where it was last drawn: endpoints are attributes on a batch child, so
      // every write is a geometry re-emit, and the turn otherwise moves every
      // line every frame. The lag is under a quarter of a pixel at the scales
      // the lines are read at, and real movement crosses the threshold at once.
      if (i.f == frm) {
        const d1 = Math.max(
          Math.abs(i.x - (linkCache._x1 === undefined ? 1e9 : linkCache._x1)),
          Math.abs(i.y - (linkCache._y1 || 0))
        );
        if (d1 > 1.5) {
          linkCache._x1 = i.x;
          linkCache._y1 = i.y;
          setAttrIfChanged(linkCache, e, "x1", i.x);
          setAttrIfChanged(linkCache, e, "y1", i.y);
        }
      }
      if (i.parent.f == frm) {
        const d2 = Math.max(
          Math.abs(
            i.parent.x - (linkCache._x2 === undefined ? 1e9 : linkCache._x2)
          ),
          Math.abs(i.parent.y - (linkCache._y2 || 0))
        );
        if (d2 > 1.5) {
          linkCache._x2 = i.parent.x;
          linkCache._y2 = i.parent.y;
          setAttrIfChanged(linkCache, e, "x2", i.parent.x);
          setAttrIfChanged(linkCache, e, "y2", i.parent.y);
        }
      }

      const hide = i.parent.hidden || i.hidden;
      setAttrIfChanged(linkCache, e, "display", hide ? "none" : "");

      const ea = i.eventActivity;
      let tot = 0;
      if (ea) {
        for (let j = logLevels.length - 1; j >= 0; j--) {
          let v = ea[logLevels[j].name];
          if (v <= 0 || !v) continue;
          tot += v;
          v = v * (1 - time / 100);
          if (v <= 1) v = 0;
          ea[logLevels[j].name] = v;
        }
      }
      // Quantised for the same reason as data-flow below: the activity total
      // decays a little every frame, and a twentieth of a unit of width is
      // finer than the line can show.
      setAttrIfChanged(
        linkCache,
        e,
        "stroke-width",
        Math.round((0.5 + Math.log(tot / 10 + 1)) * 20) / 20
      );
      setAttrIfChanged(linkCache, e, "stroke", colour);

      // How hard the link is working, for the traffic pulses.
      //
      // On its own envelope rather than reading `tot` directly: `eventActivity`
      // decays by a sixth every frame, so it is back to nothing a third of a
      // second after each counts message. That is what stroke-width wants — a
      // flicker of thickness — but pulses need to keep running between messages
      // or they strobe once a second. Rises immediately, falls over about a
      // second.
      const busy = Math.min(1, Math.log(tot / 4 + 1) / 3);
      i.flowLevel =
        busy > (i.flowLevel || 0) ? busy : (i.flowLevel || 0) * flowFade;

      // Quantised because this is an attribute on a batch child, so every
      // distinct value it takes is a geometry rebuild — and a tenth is finer than
      // the effect can show anyway.
      setAttrIfChanged(
        linkCache,
        e,
        "data-flow",
        i.flowLevel > 0.04 ? Math.round(i.flowLevel * 10) / 10 : 0
      );
    }
  }
}

function setPositions(nodes, startAngle) {
  let angle = startAngle;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];

    // `setDistance` skips hidden nodes, so they have no distance to place them at
    // and this would work out NaN. Their angle is zero either way, so stepping
    // over them costs their siblings nothing.
    if (node.hidden) continue;

    if (polarMode) {
      node.tx = angle + node.angle / 2;
      node.ty = node.distance;
    } else {
      node.tx = Math.cos(angle + node.angle / 2) * node.distance;
      node.ty = Math.sin(angle + node.angle / 2) * node.distance;
    }

    // Placed outright rather than eased into position, both when it is new and
    // when it comes back from being hidden. `undefined` alone is not enough: a
    // node that was hidden while the filters kept it out has no usable position,
    // and easing from one only ever produces another.
    if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) {
      node.x = node.tx;
      node.y = node.ty;
      if (node.elem) {
        if (polarMode) {
          node.elem.attr(
            "transform",
            `translate(${Math.cos(node.tx) * node.ty},${
              Math.sin(node.tx) * node.ty
            })`
          );
        } else {
          node.elem.attr("transform", `translate(${node.tx},${node.ty})`);
        }
      }
    }
    if (node.children.length) {
      setPositions(node.children, angle, node.angle);
    }
    angle += node.angle;
  }
}

function distributeAngles(nodes, avail) {
  const total = nodes.reduce((a, b) => a + b.angle, 0);
  // Every child hidden. Dividing by this would put NaN through the whole subtree.
  if (!total) return;
  const factor = avail / total;
  nodes.forEach((node) => {
    node.angle *= factor;
    if (node.children.length) {
      distributeAngles(node.children, node.angle);
    }
  });
}

const calcAnglesOptions = [
  calcAngles0,
  calcAngles1,
  calcAngles2,
  calcAngles3,
  calcAngles4,
  calcAngles5,
  calcAngles6,
];

let calcAngles = calcAnglesOptions[4];

export function cycleLayoutMode() {
  const lm =
    (calcAnglesOptions.indexOf(calcAngles) + 1) % calcAnglesOptions.length;
  console.log("layout mode", lm);
  calcAngles = calcAnglesOptions[lm];
}

function calcAngles0(obj, dist) {
  const {
    layout: { radius },
    hasRag,
  } = schema.objectTypes[obj.type];
  const actualRadius = radius instanceof Function ? radius(obj) : radius;
  dist += actualRadius;
  if (hasRag && obj.parent && obj.parent.children.length > 1)
    dist += ragRadius * 4;
  const angle = 1;
  dist += actualRadius;
  let sumChildAngles = 0;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    const childAngle = calcAngles0(child, dist);
    sumChildAngles += childAngle;
  }
  return (obj.angle = sumChildAngles > angle ? sumChildAngles : angle);
}

function calcAngles1(obj, dist) {
  const {
    layout: { radius },
    hasRag,
  } = schema.objectTypes[obj.type];
  const actualRadius = radius instanceof Function ? radius(obj) : radius;
  dist += actualRadius;
  if (hasRag && obj.parent && obj.parent.children.length > 1)
    dist += ragRadius * 4;
  const angle = 1;
  dist += actualRadius;
  let sumChildAngles = 0;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    const childAngle = calcAngles1(child, dist);
    sumChildAngles += childAngle;
  }
  return (obj.angle = sumChildAngles + angle);
}

function calcAngles2(obj, dist) {
  const {
    layout: { radius },
    hasRag,
  } = schema.objectTypes[obj.type];
  const actualRadius = radius instanceof Function ? radius(obj) : radius;
  dist += actualRadius;
  if (hasRag && obj.parent && obj.parent.children.length > 1)
    dist += ragRadius * 4;
  const angle = actualRadius;
  dist += actualRadius;
  let sumChildAngles = 0;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    const childAngle = calcAngles2(child, dist);
    sumChildAngles += childAngle;
  }
  return (obj.angle = sumChildAngles + angle);
}

function calcAngles3(obj, dist) {
  const {
    layout: { radius },
    hasRag,
  } = schema.objectTypes[obj.type];
  const actualRadius = radius instanceof Function ? radius(obj) : radius;
  dist += actualRadius;
  if (hasRag && obj.parent && obj.parent.children.length > 1)
    dist += ragRadius * 4;
  const angle = actualRadius;
  dist += actualRadius;
  let sumChildAngles = 0;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    const childAngle = calcAngles3(child, dist);
    sumChildAngles += childAngle;
  }
  return (obj.angle = sumChildAngles > angle ? sumChildAngles : angle);
}

function calcAngles4(obj, dist) {
  // Asks for nothing, so `distributeAngles` gives it nothing and `setPositions`
  // steps past it without advancing — a hidden node occupies no part of the ring.
  if (obj.hidden) return (obj.angle = 0);

  const {
    layout: { radius },
    hasRag,
  } = schema.objectTypes[obj.type];
  const actualRadius = radius instanceof Function ? radius(obj) : radius;
  dist += actualRadius;
  if (hasRag && obj.parent && obj.parent.children.length > 1)
    dist += ragRadius * 4;
  const angle = 2 * Math.atan2(actualRadius, dist);
  dist += actualRadius;
  let sumChildAngles = 0;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    const childAngle = calcAngles4(child, dist);
    sumChildAngles += childAngle;
  }
  return (obj.angle = sumChildAngles > angle ? sumChildAngles : angle);
}

function calcAngles5(obj, dist) {
  const {
    layout: { radius },
    hasRag,
  } = schema.objectTypes[obj.type];
  const actualRadius = radius instanceof Function ? radius(obj) : radius;
  dist += actualRadius;
  if (hasRag && obj.parent && obj.parent.children.length > 1)
    dist += ragRadius * 4;
  const angle = 2 * Math.atan2(actualRadius, dist);
  dist += actualRadius;
  let sumChildAngles = 0;
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    const childAngle = calcAngles5(child, dist);
    sumChildAngles += childAngle;
  }
  return (obj.angle = sumChildAngles + angle);
}

function calcAngles6(obj) {
  for (let i = 0; i < obj.children.length; i++) {
    const child = obj.children[i];
    calcAngles6(child);
  }
  obj.angle = 1;
}

export default layout;
